import React, { useEffect, useState } from "react";
import { Card } from "react-bootstrap";
import { Col, Row } from "reactstrap";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import K2Api from "lib/ajax/K2Api";
import DateFormat from "components/LM/common/DateFormat";
import TooltipWrapper from "components/LM/common/TooltipWrapper";

import { Loader } from "@launchmetrics/lm-ui";
import "components/LM/common/DemographicsDisplay.scss";

const GENDER_COLORS = {
  male: "#2c7be5",
  female: "#e6399b",
};
const DEFAULT_SEGMENT_COLOR = "#95a3b3";
const COUNTRY_DISPLAY_LIMIT = 15;

function DemographicsDisplay(props) {
  const { profileId, type } = props;
  const [state, setState] = useState({ loading: true, demographics: null, error: null });

  useEffect(() => {
    let cancelled = false;

    async function fetchDemographics() {
      const k2Api = new K2Api();
      try {
        const response = await k2Api.get(`/socialmedia/${type}/demographics/${profileId}`);
        if (!cancelled) {
          setState({ loading: false, demographics: response.data, error: null });
        }
      } catch (err) {
        if (cancelled) return;
        if (err.response && err.response.status === 404) {
          setState({ loading: false, demographics: null, error: null });
        } else {
          setState({ loading: false, demographics: null, error: err });
        }
      }
    }

    fetchDemographics();
    return () => { cancelled = true; };
  }, [profileId, type]);

  return (
    <Card className="demographics-display">
      <Card.Body>
        <DemographicsContent {...state} />
      </Card.Body>
    </Card>
  );
}

function DemographicsContent({ loading, error, demographics }) {
  if (loading) {
    return <Loader />;
  }

  if (error) {
    return <p className="text-danger mb-0">Failed to load demographics data</p>;
  }

  if (!demographics) {
    return <p className="text-black-50 mb-0">No demographics data available for this profile</p>;
  }

  const { gender, age, country, capture_updated_at } = demographics;

  return (
    <>
      {capture_updated_at && (
        <p className="text-black-50 details mb-3">
          Last captured: <DateFormat>{capture_updated_at}</DateFormat>
        </p>
      )}
      <Row>
        <Col md="5">
          <h5>Gender</h5>
          <GenderChart data={gender} />
        </Col>
        <Col md="7">
          <h5>Age</h5>
          <BarList items={age} />
        </Col>
      </Row>
      <Row className="mt-4">
        <Col md="12">
          <h5>Country</h5>
          <CountryList items={country} />
        </Col>
      </Row>
    </>
  );
}

function GenderChart({ data }) {
  if (!data || !data.length) {
    return <p className="text-black-50">No gender data</p>;
  }

  const segments = data.map(item => ({ ...item, color: GENDER_COLORS[item.code] || DEFAULT_SEGMENT_COLOR }));
  const chartData = segments.map(segment => ({
    name: segment.name,
    value: segment.percentage,
    color: segment.color,
  }));

  return (
    <div className="d-flex align-items-center gender-chart">
      <div className="donut-chart-wrapper">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={43}
              outerRadius={65}
              paddingAngle={0}
              stroke="none"
            >
              {chartData.map((entry, index) => (
                <Cell key={index} fill={entry.color} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </div>
      <ul className="gender-legend list-unstyled mb-0 ml-3">
        {segments.map(segment => (
          <li key={segment.code} className="d-flex align-items-center mb-1">
            <span className="legend-swatch" style={{ backgroundColor: segment.color }} />
            <span className="ml-2">{segment.name}: {formatPercentage(segment.percentage)}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CountryList({ items }) {
  const [expanded, setExpanded] = useState(false);

  if (!items || !items.length) {
    return <p className="text-black-50">No country data</p>;
  }

  const sorted = [...items].sort((first, second) => second.percentage - first.percentage);
  const visible = expanded ? sorted : sorted.slice(0, COUNTRY_DISPLAY_LIMIT);

  return (
    <>
      <BarList items={visible} />
      {sorted.length > COUNTRY_DISPLAY_LIMIT && (
        <button
          type="button"
          className="btn btn-link btn-sm p-0 mt-2"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? "Show less" : `Show all ${sorted.length} countries`}
        </button>
      )}
    </>
  );
}

function BarList({ items }) {
  if (!items || !items.length) {
    return <p className="text-black-50">No data</p>;
  }

  return (
    <div className="demographics-bar-list">
      {items.map(item => {
        const key = item.code || item.iso_code || item.name;
        return (
          <div className="demographics-bar-row" key={key}>
            <TooltipWrapper placement="top" tooltip={item.name}>
              <div className="bar-label text-ellipsis-title">{item.name}</div>
            </TooltipWrapper>
            <div className="bar-track">
              <div className="bar-fill" style={{ width: `${Math.min(item.percentage, 100)}%` }} />
            </div>
            <div className="bar-value">{formatPercentage(item.percentage)}%</div>
          </div>
        );
      })}
    </div>
  );
}

function formatPercentage(value) {
  return Number(value).toFixed(1);
}

export default DemographicsDisplay;
